import { useEffect, useRef, useState } from 'react';
import { useHeaderAnimation } from '~/hooks/useHeaderAnimation';
import { LogOutIcon, ShoppingCart, Menu, X, Bot, Shield, Timer, Pause, User } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { PERMISSIONS, useAuth } from '~/context/auth';
import { useNavigation } from '~/context/navigation';
import { NavLink, useLocation, useNavigate } from 'react-router';
import { useCart } from '~/context/purchase';
import { useActivitiesAccess } from '~/context/activities-access';
import logo from '~/assets/logo.png';
import { config } from '~/config';
import AssistantModal from '~/components/assistant-modal';
import PomodoroModal from '~/components/pomodoro-modal';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import { formatTime } from '~/helpers/formatTime';
import { toast } from 'sonner';

const POMODORO_MODE = {
  FOCUS: 'focus',
  SHORT_BREAK: 'short_break',
  LONG_BREAK: 'long_break',
} as const;

type PomodoroMode = (typeof POMODORO_MODE)[keyof typeof POMODORO_MODE];

const TIMER_STATUS = {
  RUNNING: 'running',
  PAUSED: 'paused',
} as const;

type TimerStatus = (typeof TIMER_STATUS)[keyof typeof TIMER_STATUS];

type PomodoroStorageSnapshot = {
  mode: PomodoroMode;
  status: TimerStatus;
  endAt: number | null;
  remainingSeconds: number;
};

function getPomodoroRemainingSecondsFromStorage(parsed: Partial<PomodoroStorageSnapshot>): number {
  if (parsed.status === TIMER_STATUS.PAUSED) {
    return Math.max(0, Math.trunc(parsed.remainingSeconds ?? 0));
  }
  if (typeof parsed.endAt === 'number') {
    return Math.max(0, Math.ceil((parsed.endAt - Date.now()) / 1000));
  }
  return 0;
}

const pomodoroModeLabelByMode: Record<PomodoroMode, string> = {
  [POMODORO_MODE.FOCUS]: 'Foco',
  [POMODORO_MODE.SHORT_BREAK]: 'Pausa curta',
  [POMODORO_MODE.LONG_BREAK]: 'Pausa longa',
};

function getMobileNavLinkClassName(isActive: boolean) {
  const base = 'rounded-lg px-4 py-3 text-[15px] font-medium transition-all';
  if (isActive) return `${base} bg-emerald-50 text-emerald-700`;
  return `${base} text-slate-700 hover:bg-slate-50`;
}

export default function Header() {
  const { user, logout, hasRole, isAuthenticated, login } = useAuth();
  const { openCart, getTotalItems } = useCart();
  const { hasFullAccess, onlyQuizAccess, onlyDashboardAccess } = useActivitiesAccess();
  const showDashboardTab = hasFullAccess || onlyDashboardAccess;
  const showActivitiesTab = hasFullAccess || onlyQuizAccess;
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [assistantModalOpen, setAssistantModalOpen] = useState(false);
  const [pomodoroOpen, setPomodoroOpen] = useState(false);
  const [isAdminView, setIsAdminView] = useState(false);
  const [pomodoroHeader, setPomodoroHeader] = useState<{
    isRunning: boolean;
    mode: PomodoroMode;
    remainingSeconds: number;
  }>({ isRunning: false, mode: POMODORO_MODE.LONG_BREAK, remainingSeconds: 0 });
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const pomodoroLastNotifiedEndAtRef = useRef<number | null>(null);

  const { resetNavigation } = useNavigation();

  const { tabsRef, indicatorPosition, setIndicatorPosition } = useHeaderAnimation(
    pomodoroHeader.isRunning,
  );

  const adminUsersEnabled = config.features.adminUsersEnabled;
  const adminCoursesEnabled = config.features.adminCoursesEnabled;
  const adminDashboardEnabled = config.features.adminDashboardEnabled;
  const adminTaxonomyEnabled = config.features.adminTaxonomyEnabled;

  const canAccessAdminCourses =
    adminCoursesEnabled &&
    (hasRole(PERMISSIONS.COURSES_WRITE) || hasRole(PERMISSIONS.COURSES_EDITOR));

  const canAccessAdminUsers = adminUsersEnabled && hasRole(PERMISSIONS.USERS_WRITE);

  const canAccessAdminDashboards =
    adminDashboardEnabled &&
    (hasRole(PERMISSIONS.DASHBOARDS_PURCHASES_READ) ||
      hasRole(PERMISSIONS.DASHBOARDS_ENGAGEMENTS_READ) ||
      hasRole(PERMISSIONS.DASHBOARDS_CONTENTS_READ));

  const canAccessAdminTaxonomy = adminTaxonomyEnabled && hasRole(PERMISSIONS.TAXONOMIES_MANAGE);

  const isAdmin =
    hasRole(PERMISSIONS.QUESTIONS_WRITE) ||
    canAccessAdminTaxonomy ||
    canAccessAdminUsers ||
    canAccessAdminCourses ||
    hasRole(PERMISSIONS.COUPONS_WRITE) ||
    canAccessAdminDashboards;

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target as Node)) {
        setMobileMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  useEffect(() => {
    if (!isAdmin && isAdminView) {
      setIsAdminView(false);
      return;
    }

    if (!isAdmin) {
      return;
    }

    const isAdminPath = location.pathname.startsWith('/admin/');

    if (isAdminPath && !isAdminView) {
      setIsAdminView(true);
    }

    if (!isAdminPath && isAdminView) {
      setIsAdminView(false);
    }
  }, [isAdmin, isAdminView, location.pathname]);

  const initial = (user?.firstName || user?.email || '?').trim().charAt(0).toUpperCase();

  const handleLogout = async () => {
    setMenuOpen(false);
    logout();
  };

  const handleProfileAccess = () => {
    setMenuOpen(false);
    navigate('/profile');
  };

  const handleBackHome = () => {
    resetNavigation();
    if (isAuthenticated && !hasFullAccess) {
      navigate('/cursos');
      return;
    }
    navigate('/');
  };

  const handleCartClick = () => {
    openCart();
  };

  const handleAssistantClick = () => {
    setAssistantModalOpen(true);
  };

  useEffect(() => {
    if (!isAuthenticated) {
      setPomodoroOpen(false);
      return;
    }

    const params = new URLSearchParams(location.search);
    const shouldOpenPomodoro = params.get('pomodoro') === '1' || params.get('pomodoro') === 'true';
    if (!shouldOpenPomodoro) return;

    setPomodoroOpen(true);

    params.delete('pomodoro');
    const nextSearch = params.toString();
    navigate(`${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`, { replace: true });
  }, [isAuthenticated, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const TIMER_STORAGE_KEY = 'pomodoro-timer-v1';

    const read = () => {
      try {
        const raw = window.localStorage.getItem(TIMER_STORAGE_KEY);
        if (!raw) {
          setPomodoroHeader((prev) =>
            prev.isRunning ? { ...prev, isRunning: false, remainingSeconds: 0 } : prev,
          );
          return;
        }

        const parsed = JSON.parse(raw) as Partial<PomodoroStorageSnapshot>;

        if (
          !parsed.mode ||
          (parsed.status !== TIMER_STATUS.RUNNING && parsed.status !== TIMER_STATUS.PAUSED)
        ) {
          setPomodoroHeader((prev) => ({ ...prev, isRunning: false, remainingSeconds: 0 }));
          return;
        }

        const mode = parsed.mode as PomodoroMode;

        const remaining = getPomodoroRemainingSecondsFromStorage(parsed);

        if (remaining <= 0) {
          const endAt = typeof parsed.endAt === 'number' ? parsed.endAt : null;
          const shouldNotifyPomodoroFinished =
            parsed.status === TIMER_STATUS.RUNNING && endAt !== null;

          if (shouldNotifyPomodoroFinished && pomodoroLastNotifiedEndAtRef.current !== endAt) {
            const label = pomodoroModeLabelByMode[mode];
            toast.success(`Pomodoro finalizado: ${label}`);
            pomodoroLastNotifiedEndAtRef.current = endAt;
          }
          window.localStorage.removeItem(TIMER_STORAGE_KEY);
          setPomodoroHeader({ isRunning: false, mode, remainingSeconds: 0 });
          return;
        }

        setPomodoroHeader((prev) => {
          if (prev.isRunning && prev.mode === mode && prev.remainingSeconds === remaining)
            return prev;
          return { isRunning: true, mode, remainingSeconds: remaining };
        });
      } catch {
        setPomodoroHeader((prev) => ({ ...prev, isRunning: false, remainingSeconds: 0 }));
      }
    };

    read();
    const id = window.setInterval(read, 250);
    return () => window.clearInterval(id);
  }, []);

  const getPomodoroIcon = () => {
    if (!pomodoroHeader.isRunning) {
      return Timer;
    }

    if (pomodoroHeader.mode === POMODORO_MODE.FOCUS) {
      return Timer;
    }

    return Pause;
  };

  const PomodoroIcon = getPomodoroIcon();

  const handleAdminAccess = () => {
    if (!isAdmin) {
      return;
    }

    setMenuOpen(false);

    if (!isAdminView) {
      setIsAdminView(true);

      if (canAccessAdminDashboards) {
        navigate('/admin/dashboard');
        return;
      }

      if (hasRole(PERMISSIONS.QUESTIONS_WRITE)) {
        navigate('/admin/questoes');
        return;
      }
      if (canAccessAdminTaxonomy) {
        navigate('/admin/taxonomia');
        return;
      }

      if (canAccessAdminUsers) {
        navigate('/admin/usuarios');
        return;
      }

      if (canAccessAdminCourses) {
        navigate('/admin/cursos');
        return;
      }

      if (hasRole(PERMISSIONS.COUPONS_WRITE)) {
        navigate('/admin/cupons');
        return;
      }

      return;
    }

    setIsAdminView(false);
    handleBackHome();
  };

  const handleMobileNavigate = (path: string) => {
    setMobileMenuOpen(false);
    if (path === '/') {
      handleBackHome();
      return;
    }
    navigate(path);
  };

  return (
    <header className="sticky top-0 z-50 bg-white shadow-sm">
      <div className="rounded-sm bg-white">
        <div className="grid grid-cols-[auto_1fr_auto] items-center gap-3 sm:gap-4 px-4 py-[18px]">
          <div className="flex items-center gap-2">
            {isAuthenticated && (
              <button
                type="button"
                onClick={() => setMobileMenuOpen((prev) => !prev)}
                className="inline-flex items-center justify-center p-2 rounded-md text-slate-700 hover:brightness-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/60 transition-all sm:hidden"
                aria-label="Abrir menu de navegação"
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-navigation"
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            )}
            <h1
              className="flex select-none text-2xl font-black tracking-tight text-emerald-500 cursor-pointer"
              onClick={handleBackHome}
            >
              <img src={logo} alt="Ceisc MED logo" className="h-8" />
            </h1>
          </div>

          <div className="flex items-center justify-center">
            {isAuthenticated && (
              <nav className="hidden sm:flex items-center gap-6 relative w-full flex-1 justify-center">
                <div
                  ref={tabsRef}
                  className="flex items-center justify-center w-full flex-1 relative"
                >
                  {!isAdminView && (
                    <>
                      {!config.features.marchActivitiesLocked && hasFullAccess && (
                        <Tab
                          to="/"
                          label="Início"
                          end
                          onActive={setIndicatorPosition}
                          onClick={handleBackHome}
                        />
                      )}
                      <Tab to="/cursos" label="Cursos" onActive={setIndicatorPosition} />
                      {!config.features.marchActivitiesLocked && showDashboardTab && (
                        <Tab to="/dashboard" label="Dashboard" onActive={setIndicatorPosition} />
                      )}
                      {config.features.quizEnabled && showActivitiesTab && (
                        <Tab to="/atividades" label="Atividades" onActive={setIndicatorPosition} />
                      )}
                      {!config.features.marchActivitiesLocked && hasFullAccess && (
                        <Tab to="/cronograma" label="Cronograma" onActive={setIndicatorPosition} />
                      )}
                    </>
                  )}

                  {isAdminView && isAdmin && (
                    <>
                      {canAccessAdminDashboards && (
                        <Tab
                          to="/admin/dashboard"
                          label="Início"
                          end
                          onActive={setIndicatorPosition}
                        />
                      )}
                      {config.features.quizEnabled && hasRole(PERMISSIONS.QUESTIONS_WRITE) && (
                        <Tab
                          to="/admin/questoes"
                          label="Questões e Flashcards"
                          onActive={setIndicatorPosition}
                        />
                      )}
                      {canAccessAdminTaxonomy && (
                        <Tab
                          to="/admin/taxonomia"
                          label="Taxonomia"
                          onActive={setIndicatorPosition}
                        />
                      )}
                      {canAccessAdminCourses && (
                        <Tab to="/admin/cursos" label="Cursos" onActive={setIndicatorPosition} />
                      )}
                      {!config.features.marchActivitiesLocked &&
                        hasRole(PERMISSIONS.COUPONS_WRITE) && (
                          <Tab to="/admin/cupons" label="Cupons" onActive={setIndicatorPosition} />
                        )}
                      {canAccessAdminUsers && (
                        <Tab
                          to="/admin/usuarios"
                          label="Usuários"
                          onActive={setIndicatorPosition}
                        />
                      )}
                    </>
                  )}

                  <motion.div
                    className="absolute -bottom-2 h-0.5 bg-text-dark"
                    initial={false}
                    animate={{
                      left: indicatorPosition.left,
                      width: indicatorPosition.width,
                    }}
                    transition={{
                      type: 'spring',
                      stiffness: 500,
                      damping: 35,
                    }}
                  />
                </div>
              </nav>
            )}
          </div>

          <div className="flex items-center gap-2 justify-end">
            {isAuthenticated && !isAdminView && config.features.assistantEnabled && (
              <button
                type="button"
                onClick={handleAssistantClick}
                className="inline-flex items-center justify-center p-2 text-slate-700 hover:brightness-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/60 rounded-md cursor-pointer transition-all"
                aria-label="Abrir Assistente de IA"
                title="Assistente de IA"
              >
                <Bot className="h-5 w-5" />
              </button>
            )}

            {isAuthenticated && !isAdminView && (
              <div className="shrink-0 flex">
                <Popover open={pomodoroOpen} onOpenChange={setPomodoroOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      title="Pomodoro"
                      className="inline-flex items-center justify-center gap-1.5 p-2 text-slate-700 hover:brightness-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/60 rounded-md cursor-pointer transition-all"
                    >
                      <PomodoroIcon className="h-5 w-5" aria-hidden="true" />
                      {pomodoroHeader.isRunning && (
                        <span className="text-sm font-semibold tabular-nums text-text-primary">
                          {formatTime(pomodoroHeader.remainingSeconds)}
                        </span>
                      )}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="end"
                    side="bottom"
                    sideOffset={12}
                    className="w-[360px] max-w-[calc(100vw-2rem)] p-0 overflow-hidden rounded-[12px] border border-border-tertiary bg-card shadow-md"
                  >
                    <PomodoroModal onClose={() => setPomodoroOpen(false)} />
                  </PopoverContent>
                </Popover>
              </div>
            )}

            {!isAdminView && (
              <button
                type="button"
                onClick={handleCartClick}
                className="relative inline-flex items-center justify-center p-2 text-slate-700 hover:brightness-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/60 rounded-md cursor-pointer transition-all"
                aria-label="Abrir carrinho"
                title="Carrinho"
              >
                <ShoppingCart className="h-5 w-5" />
                {getTotalItems() > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-emerald-600 text-[11px] font-bold text-white">
                    {getTotalItems()}
                  </span>
                )}
              </button>
            )}

            {isAuthenticated && (
              <div className="relative" ref={menuRef}>
                <button
                  type="button"
                  onClick={() => setMenuOpen((v) => !v)}
                  className="inline-flex h-9 w-9 select-none items-center justify-center rounded-full bg-bg-secondary text-[18px] font-semibold text-text-dark border border-border-tertiary/80 hover:brightness-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/60 cursor-pointer transition-all"
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  aria-label="Abrir menu de usuário"
                  title="Conta"
                >
                  {initial}
                </button>

                <AnimatePresence>
                  {menuOpen && (
                    <motion.div
                      role="menu"
                      className="absolute right-2 sm:right-0 mt-2 w-[calc(100vw-2rem)] sm:w-80 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl"
                      initial={{ opacity: 0, scale: 0.95, y: -10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: -10 }}
                      transition={{ duration: 0.15, ease: 'easeOut' }}
                    >
                      <div className="flex items-center gap-3 px-4 py-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-sm font-semibold text-slate-700 ring-1 ring-slate-200">
                          {initial}
                        </div>
                        <div className="min-w-0">
                          <p className="truncate text-[15px] font-semibold text-slate-900">
                            {user?.firstName} {user?.lastName}
                          </p>
                          <p className="truncate text-[13px] text-slate-500">{user?.email}</p>
                        </div>
                      </div>

                      <div className="mx-4 h-px bg-slate-200" />

                      {isAdmin && (
                        <button
                          role="menuitem"
                          onClick={handleAdminAccess}
                          className="flex w-full items-center gap-2 px-4 py-3 text-left text-[15px] font-medium text-slate-700 hover:text-slate-900 hover:bg-slate-50 cursor-pointer transition-all"
                        >
                          <Shield className="h-4 w-4" />
                          {isAdminView ? 'Acesso Aluno' : 'Acesso Administrativo'}
                        </button>
                      )}

                      <button
                        role="menuitem"
                        onClick={handleProfileAccess}
                        className="flex w-full items-center gap-2 px-4 py-3 text-left text-[15px] font-medium text-slate-700 hover:text-slate-900 hover:bg-slate-50 cursor-pointer transition-all"
                      >
                        <User className="h-4 w-4" />
                        Meu perfil
                      </button>

                      <button
                        role="menuitem"
                        onClick={handleLogout}
                        className="flex w-full items-center gap-2 px-4 py-3 text-left text-[15px] font-medium text-red-600 hover:text-red-700 hover:bg-red-50 cursor-pointer transition-all"
                      >
                        <LogOutIcon className="h-4 w-4" />
                        Sair da conta
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {!isAuthenticated && (
              <button
                type="button"
                onClick={login}
                className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-bg-core-dark text-white text-sm font-medium hover:brightness-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/60 cursor-pointer transition-all"
                aria-label="Entrar ou cadastrar-se"
              >
                Entrar ou cadastrar-se
              </button>
            )}
          </div>
        </div>
        <div className="h-px w-full bg-border-tertiary" />
      </div>
      {config.features.assistantEnabled && (
        <AssistantModal isOpen={assistantModalOpen} onClose={() => setAssistantModalOpen(false)} />
      )}
      <AnimatePresence>
        {isAuthenticated && mobileMenuOpen && (
          <motion.div
            id="mobile-navigation"
            ref={mobileMenuRef}
            className="sm:hidden border-t border-border-tertiary bg-white"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            <nav className="px-4 py-3">
              <div className="flex flex-col gap-2">
                {!isAdminView && (
                  <>
                    {!config.features.marchActivitiesLocked && hasFullAccess && (
                      <NavLink
                        to="/"
                        end
                        onClick={() => handleMobileNavigate('/')}
                        className={({ isActive }) => getMobileNavLinkClassName(isActive)}
                      >
                        Início
                      </NavLink>
                    )}
                    <NavLink
                      to="/cursos"
                      onClick={() => handleMobileNavigate('/cursos')}
                      className={({ isActive }) => getMobileNavLinkClassName(isActive)}
                    >
                      Cursos
                    </NavLink>
                    {!config.features.marchActivitiesLocked && showDashboardTab && (
                      <NavLink
                        to="/dashboard"
                        onClick={() => handleMobileNavigate('/dashboard')}
                        className={({ isActive }) => getMobileNavLinkClassName(isActive)}
                      >
                        Dashboard
                      </NavLink>
                    )}
                    {config.features.quizEnabled && showActivitiesTab && (
                      <NavLink
                        to="/atividades"
                        onClick={() => handleMobileNavigate('/atividades')}
                        className={({ isActive }) => getMobileNavLinkClassName(isActive)}
                      >
                        Atividades
                      </NavLink>
                    )}
                    {!config.features.marchActivitiesLocked && hasFullAccess && (
                      <NavLink
                        to="/cronograma"
                        onClick={() => handleMobileNavigate('/cronograma')}
                        className={({ isActive }) => getMobileNavLinkClassName(isActive)}
                      >
                        Cronograma
                      </NavLink>
                    )}
                  </>
                )}

                {isAdminView && isAdmin && (
                  <>
                    {canAccessAdminDashboards && (
                      <NavLink
                        to="/admin/dashboard"
                        end
                        onClick={() => handleMobileNavigate('/admin/dashboard')}
                        className={({ isActive }) => getMobileNavLinkClassName(isActive)}
                      >
                        Início
                      </NavLink>
                    )}
                    {config.features.quizEnabled && hasRole(PERMISSIONS.QUESTIONS_WRITE) && (
                      <NavLink
                        to="/admin/questoes"
                        onClick={() => handleMobileNavigate('/admin/questoes')}
                        className={({ isActive }) => getMobileNavLinkClassName(isActive)}
                      >
                        Questões e Flashcards
                      </NavLink>
                    )}
                    {canAccessAdminTaxonomy && (
                      <NavLink
                        to="/admin/taxonomia"
                        onClick={() => handleMobileNavigate('/admin/taxonomia')}
                        className={({ isActive }) => getMobileNavLinkClassName(isActive)}
                      >
                        Taxonomia
                      </NavLink>
                    )}
                    {canAccessAdminCourses && (
                      <NavLink
                        to="/admin/cursos"
                        onClick={() => handleMobileNavigate('/admin/cursos')}
                        className={({ isActive }) => getMobileNavLinkClassName(isActive)}
                      >
                        Cursos
                      </NavLink>
                    )}
                    {!config.features.marchActivitiesLocked &&
                      hasRole(PERMISSIONS.COUPONS_WRITE) && (
                        <NavLink
                          to="/admin/cupons"
                          onClick={() => handleMobileNavigate('/admin/cupons')}
                          className={({ isActive }) => getMobileNavLinkClassName(isActive)}
                        >
                          Cupons
                        </NavLink>
                      )}
                    {canAccessAdminUsers && (
                      <NavLink
                        to="/admin/usuarios"
                        onClick={() => handleMobileNavigate('/admin/usuarios')}
                        className={({ isActive }) => getMobileNavLinkClassName(isActive)}
                      >
                        Usuários
                      </NavLink>
                    )}
                  </>
                )}
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

function Tab({
  to,
  label,
  end,
  onActive,
  onClick,
}: {
  to: string;
  label: string;
  end?: boolean;
  onActive?: (style: { left: number; width: number }) => void;
  onClick?: () => void;
}) {
  const ref = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    if (ref.current && onActive) {
      const element = ref.current;
      const isActive = element.classList.contains('active');

      if (isActive) {
        const left = element.offsetLeft;
        const width = element.offsetWidth;
        onActive({ left, width });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, onActive]);

  return (
    <NavLink
      ref={ref}
      to={to}
      end={end}
      onClick={onClick}
      className={({ isActive }) =>
        [
          'relative inline-flex items-center px-3 py-1 text-[14px] font-medium transition-all',
          isActive ? 'text-text-dark active' : 'text-text-secondary hover:brightness-70',
        ].join(' ')
      }
    >
      {label}
    </NavLink>
  );
}
